import { HTTPMethods, HTTPRequestBuilder } from "@/builder/HTTPRequestBuilder"

/**
 * Fetches short-lived Centrifugo tokens from the Django backend.
 *
 * Connection tokens authenticate the WebSocket connection and name the
 * caller's tenant channel; subscription tokens authorize the client-side
 * subscription to exactly that channel (the backend validates the requested
 * channel against the user's active tenant, so the browser can never
 * subscribe to another tenant's channel). The subscription is client-side
 * rather than token-embedded because delta compression only works for
 * client-side subscriptions.
 */
export class CentrifugoTokenRepository {
  public async getConnectionToken(): Promise<{
    token: string | null
    channel: string | null
    retainedObjects: boolean
    changeNotifications: boolean
    status: number
  }> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.GET,
      "core/centrifugo/connection-token/"
    )
      // Treat auth/misconfig responses as expected so the shared HTTP layer does not pop an error
      // alert on every reconnect attempt during a token outage. The caller maps the status to a
      // terminal (401/403) vs retryable (503/5xx) outcome for the Centrifuge client.
      .setExpectedStatusCode([200, 401, 403, 503])
      .build()
      .execute()
    return {
      token: response.status === 200 ? response.data.token : null,
      channel: response.status === 200 ? response.data.channel : null,
      changeNotifications: response.data.changeNotifications === true,
      retainedObjects: response.data.retainedObjects === true,
      status: response.status
    }
  }

  public async getSubscriptionToken(
    channel: string
  ): Promise<{ token: string | null; status: number }> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.GET,
      "core/centrifugo/subscription-token/"
    )
      .query({ channel })
      .setExpectedStatusCode([200, 401, 403, 503])
      .build()
      .execute()
    return {
      token: response.status === 200 ? response.data.token : null,
      status: response.status
    }
  }
}
